import * as React from "preact";
import { FunctionComponent } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";

import cx from "classnames";
import sanitizeHtml from "sanitize-html";

import SVGIcon from "../../../../app/components/SVGIcon";
import utils from "../../../../helpers/utils";
import { ISearchParams } from "../../App";
import constants from "../../../../helpers/constants";

export interface ISearchBox {
  className?: string;
  query?: string;
  params: ISearchParams;
  inNav?: boolean;
  inStandalonePageBlock?: boolean;
  setNewQuery?: (params: any) => void;
  placeholder?: string;
  id: string;
  recentSearchApiUrl?: string;
  handleUpdateRecentSearches?: (data: string[]) => void;
  recentSearches: string[];
  recentSearchEnabled?: boolean;
  onClose?: () => void;
}

const SearchBox: FunctionComponent<ISearchBox> =
  ({
     className,
     query,
     params,
     inNav,
     inStandalonePageBlock,
     setNewQuery,
     placeholder,
     id,
     recentSearchApiUrl,
     handleUpdateRecentSearches,
     recentSearches,
     recentSearchEnabled,
     onClose,
   }) => {
    const [searchQuery, setSearchQuery] = useState<string | undefined>(query);
    const placeholderText = placeholder || "Search for site content &amp; literature";
    const [showRecentSearch, setShowRecentSearch] = useState<boolean>(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const formRef = useRef<HTMLFormElement>(null);
    const recentSearchRef = useRef<HTMLDivElement>(null);

    const fetchRecentSearch = async () => {
      if (!recentSearchEnabled || !recentSearchApiUrl) return;
      const response = await fetch(recentSearchApiUrl);

      if (response.status !== 200) {
        console.error(`There was a problem. Status Code: ${response.status}`);
        return;
      }

      const data = await response.json();

      if (!Array.isArray(data)) return;

      handleUpdateRecentSearches?.(data);
    };

    const handleClickOutside = (event) => {
      if (recentSearchRef.current && !recentSearchRef.current.contains(event.target)) {
        setShowRecentSearch(false);
      }
    };

    useEffect(() => {
      document.addEventListener("click", handleClickOutside, true);
      return () => {
        document.removeEventListener("click", handleClickOutside, true);
      };
    }, []);

    const newParamsString =
      ({ query, queryTyped, superCategory }:
       {
         query?: string,
         queryTyped?: boolean,
         superCategory?: string,
       }): string => {
        const newParams = Object.assign(params, {
          query: query ? sanitizeHtml(query, { nonTextTags: ['style', 'script', 'textarea', 'option', 'xmp'] }) : undefined,
          page: undefined,
          filters: undefined,
          selectedFilters: undefined,
          sort: undefined,
          sortBy: undefined,
          superCategory: superCategory ? sanitizeHtml(superCategory, { nonTextTags: ['style', 'script', 'textarea', 'option', 'xmp'] }) : undefined,
          queryTyped,
        });
        return utils.serializeParams(newParams);
      };

    const setQuery = (e: any) => {
      // Override any new params here, i.e. the new query, clear filters, pagination, etc
      e.preventDefault();
      e.stopPropagation();
      setNewQuery?.(newParamsString({
        query: searchQuery,
        queryTyped: true,
        superCategory: params.superCategory,
      }));
    };

    // Clear search term, filters, pagination, etc.
    const resetQuery = (e: any) => {
      e.preventDefault();
      e.stopPropagation();
      setSearchQuery("");
      // Only re-search on a page search & ignore if user cleared without searching (params.query only populates on a search)
      if (!inNav && params.query) {
        setNewQuery?.(newParamsString({}));
      }
    };

    const onInputClick = async () => {
      if (!recentSearchEnabled || showRecentSearch || window.innerWidth < constants.breakpoints.recentSearchBox) return;

      await fetchRecentSearch();
      setShowRecentSearch(true);
    };

    const onRecentSearchSelect = (value: string) => {
      if (!recentSearchEnabled) return;

      setSearchQuery(value);

      setTimeout(() => {
        formRef.current?.dispatchEvent?.(new Event("submit"));
        setShowRecentSearch(false);
      }, 0);
    };

    const onRecentItemKeyDown = (e: KeyboardEvent, item: string) => {
      if (e.key === "Enter") {
        e.preventDefault();
        onRecentSearchSelect(item);
      }
    };

    const onInputKeydown = async (e: KeyboardEvent) => {

      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        e.preventDefault();

        if (!showRecentSearch) {
          await onInputClick();
        } else {
          const firstItem = recentSearchRef.current?.querySelector<HTMLButtonElement>(".search-box__recent-search-item-text");

          if (!firstItem) return;


          firstItem.focus();
          setSearchQuery(firstItem.textContent.trim());
        }
      }

      if (e.key === "Tab") {
        setShowRecentSearch(false);
      }
    };

    const onRecentSearchListKeydown = (e: KeyboardEvent) => {
      e.preventDefault();
      const options = Array.from(recentSearchRef.current?.querySelectorAll<HTMLElement>(".search-box__recent-search-item-text") ?? []);

      const focused = document.activeElement as HTMLElement;
      const activeIndex = options.indexOf(focused);
      let nextIndex = activeIndex;

      if (e.key === "ArrowDown" || e.key === "ArrowUp") {

        if (e.key === "ArrowDown") {
          nextIndex = (activeIndex + 1) % options.length;
        } else if (e.key === "ArrowUp") {
          nextIndex = (activeIndex - 1 + options.length) % options.length;
        }

        const nextOption = options[nextIndex];
        nextOption.focus();
        setSearchQuery(nextOption.textContent.trim());
      }

      if (e.key === 'Tab') {
        setShowRecentSearch(false);
        const nextFocusable = utils.findNextFocusableBySibling(inputRef.current);
        if (nextFocusable) {
          nextFocusable.focus();
        }
      }
    };

    return (
      <form
        className={cx(className, "search-box__container")}
        role="search"
        onSubmit={setQuery}
        ref={formRef}
      >
        <fieldset className="search-box__fieldset">
          <legend className="visually-hidden">Search box</legend>
          <label
            className="visually-hidden"
            htmlFor={`SearchBox-${id}-${inNav && "-nav"}`}
          >
            Search
          </label>
          <div className="search-box__input-wrapper" ref={recentSearchRef}>
            <input
              id={`SearchBox-${id}-${inNav && "-nav"}`}
              type="search"
              className="search-box__input input--search"
              placeholder={placeholderText}
              value={searchQuery}
              onInput={(e: any) => setSearchQuery(e.currentTarget.value)}
              onClick={onInputClick}
              onKeyDown={onInputKeydown}
              autoComplete="off"
              aria-autocomplete="none"
              ref={inputRef}
              autoFocus={!inNav}
            />
            {recentSearchEnabled && showRecentSearch && recentSearches.length > 0 ?
              <div className="search-box__recent-search">
                <p className="search-box__recent-search-label">Recent
                  searches</p>
                <ul className="search-box__recent-search-list"
                    role="menu"
                    onKeyDown={onRecentSearchListKeydown}>
                  {recentSearches.map((item, key) => <li key={`${item}-${key}`}
                                                         className="search-box__recent-search-item">
                    <SVGIcon className="search-box__recent-search-item-icon"
                             name="clock"></SVGIcon>
                    <button type="button" className="search-box__recent-search-item-text"
                            role="menuitem"
                            onClick={() => onRecentSearchSelect(item)}
                            onKeyDown={(e) => onRecentItemKeyDown(e, item)}>
                      {item}
                    </button>
                  </li>)}

                </ul>
              </div> : null}


            {searchQuery && (
              <button
                className="search-box__button search-box__button--close cta cta--with-icon cta--close"
                type="button"
                onClick={resetQuery}
              >
                <SVGIcon
                  name="close"
                  spriteUrl="/Content/images/v3/svg-sprite.svg"
                ></SVGIcon>
              </button>
            )}
          </div>


          <button
            className="search-box__button
          search-box__button--search
          cta
          cta--outline-strong
          cta--with-icon
          cta--search"
            type="submit"
          >
            <SVGIcon
              className="icon cta--icon search-icon search-box__button-icon"
              name="search"
            ></SVGIcon>

            <span
              className="cta--with-icon-text search-link-text-toggled">Search</span>
          </button>
        </fieldset>

        {inNav && !inStandalonePageBlock && (
          <button
            type="button"
            className="search-box__close js-nav-search-close unbutton"
            onClick={onClose}
          >Close</button>
        )}
      </form>
    );
  };

export default SearchBox;
